import {
  Question,
  QuestionData,
  QuestionnaireData,
  Answer,
  AnswerMap,
  Page,
  PageGroup,
  Questionnaire,
} from "../context/questionnaire/types";

const DEFAULT_PRIVACY_URL = "https://canceriq.com/privacy-policy";

export function resolveLandingConfig(data?: QuestionnaireData) {
  return {
    splashTitle: data?.pp_splash_title ?? data?.splash_title,
    splashImage: data?.pp_splash_image ?? data?.splash_image,
    surveyDescription: data?.pp_survey_description ?? data?.survey_description,
    privacyPolicyUrl:
      data?.pp_privacy_policy_url ??
      data?.privacy_policy_url ??
      DEFAULT_PRIVACY_URL,
    startButtonTitle: data?.start_button_title,
  };
}

type OperatorFn = (a: any, b: any) => boolean;

const operators: Record<string, OperatorFn> = {
  "+": (a, b) => a + b,
  "<": (a, b) => a < b,
  ">": (a, b) => a > b,
  "<=": (a, b) => a <= b,
  ">=": (a, b) => a >= b,
  "==": (a, b) => a == b,
  has: (a, b) =>
    Array.isArray(a) || typeof a === "string" ? a.indexOf(b) > -1 : false,
};

function applyOperator(
  operator: string,
  lop: any,
  rop: any,
  inverse?: string | boolean
): boolean {
  const fn = operators[operator];
  if (!fn) {
    console.error("Unknown operator:", operator);
    return false;
  }
  const result = fn(lop, rop);
  return inverse === "true" || inverse === true ? !result : result;
}

function testCondition(
  cond: {
    operator: string;
    value: any;
    inverse?: string | boolean;
    "question-name": string;
    key?: string;
  },
  answer: Answer | undefined
): boolean {
  if (answer === undefined || answer === null) {
    return false;
  }

  const numericAnswer = parseFloat(answer.value);
  if (answer.value === numericAnswer) {
    return applyOperator(
      cond.operator,
      numericAnswer,
      cond.value,
      cond.inverse
    );
  }

  if (!answer.isValid) return false;

  const questionType = answer.question?.data?.type;

  if (questionType === "choice_multi") {
    const values = (answer.values || []).map((v: any) => v.value);
    return applyOperator(cond.operator, values, cond.value, cond.inverse);
  }

  if (questionType === "columns_select_number") {
    const values = (answer.value || [])
      .filter((v: any) => v.selectVal !== undefined)
      .map((v: any) => v.selectVal.value);
    return applyOperator(cond.operator, values, cond.value, cond.inverse);
  }

  if (questionType === "multi_checkbox") {
    const values = Object.keys(answer.value || {}).filter(
      (k) => answer.value[k]
    );
    return applyOperator(cond.operator, values, cond.value, cond.inverse);
  }

  const value =
    cond.key !== undefined ? answer.value[cond.key] : answer.value;
  return applyOperator(cond.operator, value, cond.value, cond.inverse);
}

function getAnswerByName(
  answers: AnswerMap,
  name: string,
  _checking?: Set<string>
): Answer | undefined {
  const answer = answers[name];
  if (!answer) return undefined;
  const refQuestion = answer.question;
  if (refQuestion?.data?.conditions || refQuestion?.data?.conditionsOr) {
    const checking = _checking || new Set<string>();
    if (checking.has(name)) return answer;
    checking.add(name);
    if (!evaluateConditionsInner(refQuestion, answers, checking)) return undefined;
  }
  return answer;
}

function evaluateConditionsInner(
  question: Question,
  answers: AnswerMap,
  checking: Set<string>
): boolean {
  if (!question.data) {
    return true;
  }
  const conditions = question.data.conditions;
  const conditionsOr = question.data.conditionsOr;

  let conditionsMet = true;
  let conditionsOrMet = false;

  if (!conditions) conditionsMet = true;
  if (!conditionsOr) conditionsOrMet = true;

  if (conditions) {
    for (const cond of conditions) {
      const answer = getAnswerByName(answers, cond["question-name"], checking);
      conditionsMet = testCondition(cond, answer);
      if (!conditionsMet) break;
    }
  }

  if (conditionsOr) {
    for (const cond of conditionsOr) {
      const answer = getAnswerByName(answers, cond["question-name"], checking);
      if (testCondition(cond, answer)) {
        conditionsOrMet = true;
      }
    }
  }

  return conditionsMet && conditionsOrMet;
}

export function evaluateConditions(
  question: Question,
  answers: AnswerMap
): boolean {
  return evaluateConditionsInner(question, answers, new Set());
}

export function buildPages(
  questionnaire: Questionnaire | null,
  questions: Question[]
): Page[] {
  if (!questionnaire?.survey_modules) return [];

  const nameToGroup: Record<string, PageGroup> = {};

  for (const mod of questionnaire.survey_modules) {
    const moduleGroups = mod.data?.page_groups || [];
    for (const grp of moduleGroups) {
      if (!grp?.questions || !Array.isArray(grp.questions)) continue;
      for (const qName of grp.questions) {
        if (!qName) continue;
        if (nameToGroup[qName] == null) {
          nameToGroup[qName] = grp;
        }
      }
    }
  }

  const pages: Page[] = [];
  let i = 0;

  while (i < questions.length) {
    const question = questions[i];
    const qName = question.data?.name;
    const group = qName ? (nameToGroup[qName] ?? null) : null;
    const groupId = group?.id;

    const page: Page = {
      startIndex: i,
      endIndex: i,
      group,
      questions: [{ index: i, data: question.data }],
    };

    if (groupId) {
      let j = i + 1;
      while (j < questions.length) {
        const next = questions[j];
        const nextName = next.data?.name;
        const nextGroup = nextName ? (nameToGroup[nextName] ?? null) : null;
        if (nextGroup?.id === groupId) {
          page.questions.push({ index: j, data: next.data });
          page.endIndex = j;
          j += 1;
        } else {
          break;
        }
      }
      i = j;
    } else {
      i += 1;
    }

    pages.push(page);
  }

  return pages;
}

export function getAnswerKey(answer: Answer): string {
  const questionData = answer.question.data;
  return questionData.answer_key || questionData.name;
}

export function formatAnswer(answer: Answer): any {
  const question = answer.question.data;
  const basicAnswers = [
    "choice",
    "date_input",
    "select",
    "number",
    "multi_checkbox",
  ];

  if (basicAnswers.includes(question.type)) {
    return answer.value;
  }

  switch (question.type) {
    case "choice_multi": {
      return (answer.values || []).map((v: any) => v.value);
    }

    case "columns_select_number": {
      const names = question.answer_names || ["column1", "column2"];
      return (answer.value || [])
        .filter((v: any) => v.selectVal?.value !== undefined)
        .map((v: any) => {
          const entry: Record<string, any> = {};
          if (v.id !== undefined) entry.id = v.id;
          entry[names[0]] = v.selectVal.value;
          entry[names[1]] = v.inputText;
          return entry;
        });
    }

    case "choice_multi_dropdown": {
      return (answer.values || []).map((v: any) => {
        const entry: Record<string, any> = { value: v.value };
        if (v.selectVal) {
          const col2Name = question.dropdown_name || "column2";
          entry[col2Name] = v.selectVal.value;
        }
        return entry;
      });
    }

    case "columns_two_input": {
      const names = question.answer_names || ["column1", "column2"];
      return (answer.value || [])
        .filter((v: any) => v.inputText1 !== undefined)
        .map((v: any) => {
          const entry: Record<string, any> = {};
          if (v.id !== undefined) entry.id = v.id;
          entry[names[0]] = v.inputText1;
          entry[names[1]] = v.inputText2;
          return entry;
        });
    }

    case "phone_number": {
      const phoneValues = answer.values || [];
      const primaryPhone = phoneValues[0]?.value;
      if (primaryPhone === undefined) return answer.values;
      return { primaryPhone, cellphone: primaryPhone };
    }

    case "insurance":
    case "contact_info":
      return answer.values;

    case "autocomplete":
      return question.autocomplete
        ? { [question.autocomplete.name]: (answer.values as any)?.autocomplete }
        : answer.value;

    case "multi_column_nested":
    case "multi_column_nested_expansion": {
      const mainRows = answer.value || [];
      return mainRows
        .filter((row: any) => row.is_valid_row)
        .map((row: any) => ({
          ...row,
          nested_rows: (row.nested_rows || []).filter(
            (nr: any) => nr.is_valid_row
          ),
        }));
    }

    default:
      return answer.value;
  }
}

function isObject(val: any): val is Record<string, any> {
  return val !== null && typeof val === "object" && !Array.isArray(val);
}

const NON_INPUT_TYPES = new Set(["display_text", "extended_display_text", "video"]);

export function getPageErrors(
  page: Page,
  questions: Question[],
  answers: AnswerMap,
): Set<string> {
  const errors = new Set<string>();
  for (const pq of page.questions) {
    const question = questions[pq.index];
    if (!question) continue;
    const qd = question.data;
    if (NON_INPUT_TYPES.has(qd.type)) continue;
    if (!evaluateConditions(question, answers)) continue;

    const answer = answers[qd.name];
    if (qd.required && (!answer || !answer.isValid)) {
      errors.add(qd.name);
    } else if (answer && !answer.isValid) {
      errors.add(qd.name);
    }
  }
  return errors;
}

export function buildResponsePayload(
  questionnaire: Questionnaire,
  answers: AnswerMap,
  locale: string
): { survey: any } {
  const answerDict: Record<
    string,
    { answer: any; question: QuestionData }
  > = {};

  for (const key of Object.keys(answers)) {
    const answer = answers[key];
    if (!answer.isValid) continue;
    if (!evaluateConditions(answer.question, answers)) continue;

    const formatted = formatAnswer(answer);
    if (formatted === undefined) continue;

    const ansKey = getAnswerKey(answer);

    if (answerDict[ansKey] != null) {
      const previous = answerDict[ansKey].answer;
      if (Array.isArray(previous) && Array.isArray(formatted)) {
        previous.push(...formatted);
      } else if (isObject(previous) && isObject(formatted)) {
        Object.assign(previous, formatted);
      } else {
        answerDict[ansKey].answer = answer.question.data.is_part_of
          ? { [answer.question.data.name]: formatted }
          : formatted;
      }
    } else {
      answerDict[ansKey] = {
        answer: answer.question.data.is_part_of
          ? { [answer.question.data.name]: formatted }
          : formatted,
        question: answer.question.data,
      };
    }
  }

  const now = new Date();
  const uid =
    crypto.randomUUID?.() ??
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return {
    survey: {
      _id: `response_${questionnaire.id}_${uid}`,
      date: now.toDateString(),
      time: now.toTimeString(),
      survey_name: questionnaire.name,
      data: questionnaire.data,
      status: "Complete",
      type: "response",
      locale,
      modules: {
        ciq_risk_module: {
          title: questionnaire.title,
          items: answerDict,
        },
      },
    },
  };
}
